import { createBrowserRouter, Outlet } from 'react-router-dom';
import { SurveyRequestPage } from './Pages/SurveyRequest';
import { FindOutMorePage } from './Pages/FindOutMore';
import ErrorPage from './Pages/Error';
import { PrivacyPolicyPage } from './Pages/PrivacyPolicy';
import { TermsAndConditionsPage } from './Pages/TermsAndConditions';
import { DashboardPage } from './Pages/Dashboard';
import { SurveyResultPage } from './Pages/SurveyResults';
import { ResultUploadPage } from './Pages/ResultsUpload';
import { VerifyEmailPage } from './Pages/VerifyEmail';
import { ProductsPage } from './Pages/Products';
import { Res } from './resources';
import { ProjectsPage } from './Pages/Projects';
import PricingTokenInfo from './Pages/Pricing/tokenInfo';
import MyDashboard from './Pages/MyDashboard';
import { HeaderComponent } from './Components/Header';
import { FooterComponent } from './Components/Footer';
import { ProgressPopup } from './Components/ProgressPopup';
import { Box } from '@mui/material';
import AuthenticatedApp from './core/auth/AuthenticatedApp';
import FaqPage from './Pages/Faq';
import { ProgressProvider } from './core/contexts/progress.context';
import ReleaseNotesPage from './Pages/ReleaseNotes';
import { EULAPage } from './Pages/EULA';
import AdminPanel from './Pages/AdminPanel';
import ResellerDashboard from './Pages/AdminPanel/resellerdashboard';
import Accounts from './Pages/Accounts';
import { useUser } from './core/hooks';

const AdminOnlyRoute = ({ children }: { children: JSX.Element }) => {
  const { hasPermission, isLoading } = useUser();

  if (isLoading) return <Box flex={1} />;
  if (!hasPermission('ADMIN:usage')) return <ErrorPage />;

  return children;
};

export const router = createBrowserRouter([
  {
    element: (
      <AuthenticatedApp>
        <ProgressProvider>
          <HeaderComponent />
          <Box flex={1}>
            <Outlet />
          </Box>
          <FooterComponent />
          <ProgressPopup />
        </ProgressProvider>
      </AuthenticatedApp>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: Res.feature_flags.ENABLE_DASHBOARD ? <DashboardPage /> : <SurveyRequestPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/survey-request',
        element: <SurveyRequestPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/result-upload',
        element: Res.feature_flags.ENABLE_RESULT_UPLOAD ? <ResultUploadPage /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/survey-result/:projectId',
        element: Res.feature_flags.ENABLE_VIEW_RESULTS ? <SurveyResultPage /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/dashboard',
        element: Res.feature_flags.ENABLE_DASHBOARD ? <DashboardPage /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/mydashboard',
        element: Res.feature_flags.ENABLE_MY_DASHBOARD ? <MyDashboard /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/accounts',
        element: Res.feature_flags.ENABLE_MY_DASHBOARD ? <Accounts /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/projects',
        element: Res.feature_flags.ENABLE_PROJECTS ? <ProjectsPage /> : <ErrorPage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/find-out-more/:productName',
        element: <FindOutMorePage />,
        errorElement: <ErrorPage />,
      },
      {
        path: '/error',
        element: <ErrorPage />,
      },
      {
        path: '/privacy-policy',
        element: <PrivacyPolicyPage />,
      },
      {
        path: '/terms-and-conditions',
        element: <TermsAndConditionsPage />,
      },
      {
        path: '/end-user-licence-agreement',
        element: <EULAPage />,
      },
      {
        path: '/release-notes',
        element: <ReleaseNotesPage />,
      },
      {
        path: '/verify-email',
        element: <VerifyEmailPage />,
      },
      {
        path: '/products',
        element: Res.feature_flags.ENABLE_PRODUCT_MANAGEMENT ? <ProductsPage /> : <ErrorPage />,
      },
      {
        path: '/reseller/admin-panel',
        element: Res.feature_flags.ENABLE_ADMIN_PANEL ? (
          <AdminOnlyRoute>
            <AdminPanel />
          </AdminOnlyRoute>
        ) : (
          <ErrorPage />
        ),
      },
      {
        path: '/reseller/admin-panel/:userId',
        element: Res.feature_flags.ENABLE_ADMIN_PANEL ? (
          <AdminOnlyRoute>
            <ResellerDashboard />
          </AdminOnlyRoute>
        ) : (
          <ErrorPage />
        ),
      },

      {
        path: '/pricing/token_info',
        element: Res.feature_flags.ENABLE_PRICING_TOKEN_INFO ? <PricingTokenInfo /> : <ErrorPage />,
      },
      {
        path: '/faq',
        element: Res.feature_flags.ENABLE_FAQ ? <FaqPage /> : <ErrorPage />,
      },
      {
        path: '*',
        element: <ErrorPage />,
      },
    ],
  },
]);
